<ng-template #formBody>
  @let form = reviewFormService.form;

  <app-review-form-suspected-theft [form]="form"></app-review-form-suspected-theft>

  @if (reviewFormService.form.controls.isTheft.value) {
    @if (isLoading()) {
      <app-pan-blank-slate>
        <app-pan-spinner></app-pan-spinner>
        &nbsp;Loading...
      </app-pan-blank-slate>
    } @else {
      <app-review-form-recent-walk-ins [form]="form" [snapshots]="recentWalkins()" [locationTimezone]="locationTimezone()"></app-review-form-recent-walk-ins>

      <div class="flex flex-col md:flex-row gap-2">
        <div class="flex-1">
          <app-review-form-incident-date
            [form]="form"
            [timezone]="locationTimezone()"
            [displayTimezone]="true"
            [readonly]="!incidentDateEditable()">
          </app-review-form-incident-date>

          <app-review-form-incident-type [form]="form"></app-review-form-incident-type>

          <app-review-form-template [incidentForm]="form" [form]="reviewFormService.findingsForm"></app-review-form-template>

          <app-review-form-suspected-items [form]="form"></app-review-form-suspected-items>

          <app-review-form-value-additional-stolen-items [form]="form"></app-review-form-value-additional-stolen-items>

          <app-review-form-incident-photos
            [imageUrls]="reviewFormService.uniqueInstancePhotoUrls()"
            (upload)="reviewFormService.onPhotosFileUpload($event)"
            [hasKioskPhoto]="reviewFormService.hasKioskPhoto()"
          ></app-review-form-incident-photos>

          <app-review-form-transaction-reports
            [imageUrls]="reviewFormService.uniqueTransactionPhotoUrls()"
            [form]="form"
            [isRequired]="false"
            (upload)="reviewFormService.processReportsFileEvent($event)"
          ></app-review-form-transaction-reports>

          <app-review-form-extra-links [form]="form" [atLeastOneRequired]="isExtraLinkRequired()"></app-review-form-extra-links>
        </div>

        <app-review-form-suspicious-incident-report-options
          class="block flex-1"
          [form]="form"
          [caseImages]="reviewFormService.caseImages()"
          [isLoadingCaseImages]="reviewFormService.isLoadingCaseImages()"
        ></app-review-form-suspicious-incident-report-options>
      </div>

      <app-review-form-errors-inline-alert [form]="form"></app-review-form-errors-inline-alert>
    }
  } @else {
    <app-review-form-ntf-reason [form]="reviewFormService.findingsForm"></app-review-form-ntf-reason>
  }
</ng-template>

@if (isLoading()) {
  <app-pan-blank-slate>
    <app-pan-spinner></app-pan-spinner>
    &nbsp;Loading...
  </app-pan-blank-slate>
}

<!-- Transactions -->
<div class="flex flex-row max-xl:flex-col" [class.flex-wrap]="videoExpanded()">
  @if (cancellationUniqueId()) {
    <div class="p-4" [ngClass]="videoExpanded() ? 'basis-full' : 'basis-1/2'">
      <app-pan-card header="Suspect">
        <app-suspect-image
          [cancellationId]="cancellationUniqueId()"
          (hasKioskPhoto)="reviewFormService.hasKioskPhoto.set($event)"
        ></app-suspect-image>
        <app-review-video-clips
          [videoClips]="videoClips()"
          (report)="submitNotUseful($event)"
          (expandedChange)="videoExpanded.set($event)"
        ></app-review-video-clips>
      </app-pan-card>
      @if (reviewFormService.initialTransactionPhotoUrls().length > 0){
        <app-pan-card header="Transaction Reports">
        @for (report of reviewFormService.initialTransactionPhotoUrls(); track report;) {
          <div class="flex justify-center items-center">
            <img
              [src]="report"
              alt="Transactions report image"
              class="block max-w-full max-h-[50vh]"
              data-testid="reportImage"
            />
          </div>
        }
        </app-pan-card>
      }
    </div>
  }

  <div class="basis-1/2 p-4 gap-8">
    @if (timelineRows().length > 0) {
      <app-pan-card header="Timeline">
        <app-incident-timeline-table
          [rows]="timelineRows()"
          [showViewMore]="hasMoreTransactions()"
          [noMoreData]="noMoreData()"
          [isLoading]="isLoadingMoreTransactions()"
          (viewMore)="loadMoreTransactions()"
        ></app-incident-timeline-table>
      </app-pan-card>
    }

    @if (showLpCameras()) {
      <app-pan-card header="Cameras">
        @if (oneNotUsefulSubmitted()) {
          <app-pan-inline-alert type="warning" class="mb-2">
            You reported that clip(s) were not useful, please use the livestream links below.
          </app-pan-inline-alert>
        }
        @for (link of lpCameraLinks(); track link.url; let i = $index) {
          <a
            panButton
            type="button"
            target="_blank"
            class="mr-2"
            [href]="link.url"
            [attr.data-testid]="'lpCameraLink' + (i + 1)"
          >
            Camera {{ i + 1 }}
          </a>
        } @empty {
          <app-pan-inline-alert type="warning">
            There's no cameras configured for this location in Panoptyc App. Please use the branch and location information to look up the
            camera manually.
          </app-pan-inline-alert>
        }
      </app-pan-card>
    }

    <app-pan-card [header]="showLpCameras() ? 'Rhombus' : 'Cameras'">
      @if (showLpCameras()) {
        <app-pan-inline-alert type="warning" class="mb-2">
          Rhombus links are deprecated. Use the cameras above instead.
        </app-pan-inline-alert>
      }
      @if (!showLpCameras() && oneNotUsefulSubmitted()) {
        <app-pan-inline-alert type="warning" class="mb-2">
          You reported that clip(s) were not useful, please use the livestream links below.
        </app-pan-inline-alert>
      }
      @for (camera of liveCameras(); track camera; let i = $index) {
        <a
          panButton
          type="button"
          target="_blank"
          class="mr-2"
          [href]="camera.live_stream_link"
          [attr.data-testid]="'cameraLink' + (i + 1)"
        >
          Camera {{ i + 1 }}
        </a>
      } @empty {
        <app-pan-inline-alert type="warning">
          There's no cameras configured for this location in Panoptyc App. Please use the branch and location information to look up the
          camera manually.
        </app-pan-inline-alert>
      }
    </app-pan-card>

    @if (location()) {
      <app-pan-card header="Location Info">
        Branch: {{ location()?.branch_name }}<br />
        Location: {{ location()?.name }}<br />
        <span class="text-xs">(branch_id: {{ location()?.branch_id }}, location_id: {{ location()?.id }})</span>
      </app-pan-card>
    }

    @if (instructions() && instructions()?.length > 0) {
      <app-pan-card header="Instructions">
        <textarea panTextarea readonly rows="12" data-testid="instructions" value="{{ instructions() }}"> </textarea>
      </app-pan-card>
    }
  </div>
</div>
